<template>
  <div id="app">
    <BackgroundLayer 
      :background-url="currentBackground" 
      :is-background-error="isBackgroundError"
    />
    
    
    <EffectsLayer 
      ref="effectsLayerRef"
      :current-effect="currentEffect" 
      @effect-toggled="currentEffect = $event"
    />
    
    <div class="content-container">
      <InfoBar v-show="isControlsVisible" />
      
      <QuoteCard 
        :quote="currentQuote"
        :is-transitioning="isQuoteTransitioning"
      />
    </div>

    <EffectsControl 
      v-show="isControlsVisible"
      :current-effect="currentEffect"
      @toggle-effect="handleToggleEffect"
    />

    <ActionButtons
      v-show="isControlsVisible" 
      :is-background-loading="isBackgroundLoading"
      :is-quote-loading="isQuoteLoading"
      :is-background-visible="!isBackgroundError"
      @refresh-background="handleRefreshBackground"
      @refresh-quote="handleRefreshQuote"
    />

    <MusicPlayer 
      v-show="isControlsVisible"
      :current-track="musicCurrentTrack"
      :is-playing="musicIsPlaying"
      :is-muted="musicIsMuted"
      :volume="musicVolume"
      :duration="musicDuration"
      :current-time="musicCurrentTime"
      :is-loading="musicIsLoading"
      @toggle-play="musicTogglePlay"
      @next-track="musicNextTrack"
      @toggle-mute="musicToggleMute"
      @set-volume="musicSetVolume"
      @toggle-playlist="isPlaylistVisible = true"
    />

    <PlaylistModal
      :is-visible="isPlaylistVisible"
      :all-tracks="musicAllTracks"
      :current-track-index="musicCurrentTrackIndex"
      :is-playing="musicIsPlaying"
      @close="isPlaylistVisible = false"
      @play-track="handlePlayTrack"
      @remove-track="handleRemoveTrack"
    />

    <SettingsModal
      :is-visible="isSettingsVisible"
      :uploaded-tracks="musicUploadedTracks"
      :is-background-visible="!isBackgroundError"
      :volume="musicVolume"
      :is-muted="musicIsMuted"
      @close="isSettingsVisible = false"
      @upload-track="handleUploadTrack"
      @remove-track="handleRemoveTrack"
      @toggle-background="handleToggleBackground"
      @set-volume="musicSetVolume"
      @toggle-mute="musicToggleMute"
    />

    <!-- 设置按钮 -->
    <button class="settings-button" @click="isSettingsVisible = true" title="设置" v-show="isControlsVisible">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    </button>

    <!-- 功能控制按钮 -->
    <button class="toggle-controls-button" @click="isControlsVisible = !isControlsVisible" :title="isControlsVisible ? '隐藏功能' : '显示功能'">
      <svg v-if="!isControlsVisible" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </svg>
      <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </svg>
    </button>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, watchEffect } from 'vue'
import BackgroundLayer from './components/BackgroundLayer.vue'
import EffectsLayer from './components/EffectsLayer.vue'
import InfoBar from './components/InfoBar.vue'
import QuoteCard from './components/QuoteCard.vue'
import EffectsControl from './components/EffectsControl.vue'
import ActionButtons from './components/ActionButtons.vue'
import MusicPlayer from './components/MusicPlayer.vue'
import SettingsModal from './components/SettingsModal.vue'
import PlaylistModal from './components/PlaylistModal.vue'

import { useQuotes } from './composables/useQuotes.js'
import { useBackground } from './composables/useBackground.js'
import { useMusic } from './composables/useMusic.js'
import { backgrounds } from './data/backgrounds.js'
import { useI18n } from './i18n'
import { useSEO } from './seo'

export default {
  name: 'App',
  components: {
    BackgroundLayer,
    EffectsLayer,
    InfoBar,
    QuoteCard,
    EffectsControl,
    ActionButtons,
    MusicPlayer,
    SettingsModal,
    PlaylistModal
  },
  setup() {
    // 引用和状态
    const effectsLayerRef = ref(null)
    const isQuoteTransitioning = ref(false)
    const isQuoteLoading = ref(false)
    const isBackgroundLoading = ref(false)
    const currentEffect = ref(null)
    const isSettingsVisible = ref(false)
    const isControlsVisible = ref(true)
    const isPlaylistVisible = ref(false)
    
    // 使用组合式函数
    const { currentQuote, loadRandomQuote } = useQuotes()
    const { currentLanguage } = useI18n()
    const { updateSEOMeta, addStructuredData } = useSEO()
    const { 
      currentBackground, 
      currentBackgroundIndex,
      isBackgroundError, 
      preloadImageForRefresh,
      toggleTransparentBackground
    } = useBackground()
    const { 
      currentTrack: musicCurrentTrack,
      isPlaying: musicIsPlaying,
      isMuted: musicIsMuted,
      volume: musicVolume,
      duration: musicDuration,
      currentTime: musicCurrentTime,
      isLoading: musicIsLoading,
      initAudioPlayer,
      togglePlay: musicTogglePlay,
      nextTrack: musicNextTrack,
      toggleMute: musicToggleMute,
      setVolume: musicSetVolume,
      addUploadedTrack,
      removeUploadedTrack,
      uploadedTracks: musicUploadedTracks,
      allTracks: musicAllTracks,
      currentTrackIndex: musicCurrentTrackIndex,
      playTrack: musicPlayTrack
    } = useMusic()
    // 移除这行，currentEffect现在在上面定义
    
    // 处理函数
    const handleRefreshQuote = () => {
      isQuoteLoading.value = true
      isQuoteTransitioning.value = true
      
      setTimeout(() => {
        loadRandomQuote()
        isQuoteTransitioning.value = false
        
        setTimeout(() => {
          isQuoteLoading.value = false
        }, 100)
      }, 250)
    }
    
    const handleRefreshBackground = async () => {
      isBackgroundLoading.value = true
      
      setTimeout(async () => {
        const currentIndex = currentBackgroundIndex.value
        const availableIndexes = backgrounds
          .map((_, index) => index)
          .filter(index => index !== currentIndex)
        
        // 如果没有其他背景可选，直接结束
        if (availableIndexes.length === 0) {
          isBackgroundLoading.value = false
          return
        }
        
        // 最多尝试3个不同的背景
        const maxRetries = Math.min(3, availableIndexes.length)
        let successfulLoad = false
        
        for (let attempt = 0; attempt < maxRetries && !successfulLoad; attempt++) {
          // 随机选择一个可用的背景索引
          const randomIndex = Math.floor(Math.random() * availableIndexes.length)
          const newBgIndex = availableIndexes[randomIndex]
          const newBgUrl = backgrounds[newBgIndex]
          
          try {
            // 尝试预加载新背景图片
            await preloadImageForRefresh(newBgUrl)
            // 加载成功，切换背景
            currentBackgroundIndex.value = newBgIndex
            successfulLoad = true
          } catch (error) {
            // 从可用列表中移除失败的索引，避免重复尝试
            availableIndexes.splice(randomIndex, 1)
          }
        }
        
        setTimeout(() => {
          isBackgroundLoading.value = false
        }, 300)
      }, 200)
    }
    
    const handleToggleEffect = (effectName) => {
      if (effectsLayerRef.value) {
        effectsLayerRef.value.toggleEffectExternal(effectName)
      }
    }
    
    const handleToggleBackground = () => {
      toggleTransparentBackground()
    }
    
    const handleUploadTrack = async (file) => {
      try {
        await addUploadedTrack(file)
      } catch (error) {
        console.error('上传音乐失败:', error)
      }
    }
    
    const handleRemoveTrack = (index) => {
      removeUploadedTrack(index)
    }
    
    const handlePlayTrack = (index) => {
      musicPlayTrack(index)
    }
    
    
    // 键盘快捷键
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault()
        handleRefreshQuote()
      } else if (e.code === 'KeyB') {
        e.preventDefault()
        handleRefreshBackground()
      } else if (e.code === 'KeyM') {
        e.preventDefault()
        musicTogglePlay()
      } else if (e.code === 'KeyN') {
        e.preventDefault()
        musicNextTrack()
      } else if (e.code === 'Digit1') {
        e.preventDefault()
        handleToggleEffect('rain')
      } else if (e.code === 'Digit2') {
        e.preventDefault()
        handleToggleEffect('snow')
      } else if (e.code === 'Digit3') {
        e.preventDefault()
        handleToggleEffect('stars')
      } else if (e.code === 'ArrowUp') {
        e.preventDefault()
        // 音量增加 10%
        const newVolume = Math.min(100, musicVolume.value + 10)
        musicSetVolume(newVolume)
      } else if (e.code === 'ArrowDown') {
        e.preventDefault()
        // 音量减少 10%
        const newVolume = Math.max(0, musicVolume.value - 10)
        musicSetVolume(newVolume)
      } else if (e.code === 'KeyV') {
        e.preventDefault()
        // 切换静音
        musicToggleMute()
      }
    }
    
    // 生命周期
    onMounted(() => {
      // loadRandomQuote() // 不需要调用，useQuotes已经初始化了随机引言
      initAudioPlayer()
      
      document.addEventListener('keydown', handleKeyDown)
      
      // iOS Safari 初始渲染触发和PWA优化
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        // 设置viewport元标签以确保正确的渲染
        const viewport = document.querySelector('meta[name=viewport]')
        if (viewport && !viewport.content.includes('viewport-fit=cover')) {
          viewport.content = 'width=device-width,initial-scale=1.0,viewport-fit=cover,user-scalable=no,minimal-ui'
        }
        
        // 触发初始重绘
        setTimeout(() => {
          document.body.style.transform = 'translateZ(0)'
          setTimeout(() => {
            document.body.style.transform = ''
          }, 100)
        }, 500)
        
        // 强制重新计算布局以确保safe area生效
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'))
        }, 1000)
      }
      
      // PWA 安装提示（Android Chrome）
      let deferredPrompt
      window.addEventListener('beforeinstallprompt', (e) => {
        // 阻止默认的安装提示
        e.preventDefault()
        deferredPrompt = e
        
        // 5秒后显示自定义安装提示
        setTimeout(() => {
          if (deferredPrompt && !window.matchMedia('(display-mode: standalone)').matches) {
            console.log('PWA安装可用 - 可在Chrome菜单中选择"安装应用"')
          }
        }, 5000)
      })
      
      // 监听PWA安装事件
      window.addEventListener('appinstalled', (evt) => {
        console.log('PWA安装成功！')
        deferredPrompt = null
      })
      
      // 分离 iOS 和 Android PWA 处理
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      const isAndroid = /Android/.test(navigator.userAgent)
      
      // iOS PWA 处理
      if (isIOS && (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone)) {
        // iOS PWA 模式不需要额外处理，CSS已经处理了safe-area
        console.log('iOS PWA模式激活')
      }
      
      // Android PWA 全屏强制设置
      if (isAndroid && window.matchMedia('(display-mode: standalone)').matches) {
        // 强制隐藏地址栏（仅Android）
        setTimeout(() => {
          window.scrollTo(0, 1)
        }, 100)
        
        // 监听窗口大小变化，确保全屏
        window.addEventListener('resize', () => {
          setTimeout(() => {
            window.scrollTo(0, 1)
          }, 100)
        })
        
        console.log('Android PWA模式激活')
      }
      
      
      
      // 监听背景状态变化，切换html和body类名
      const updateBodyClass = () => {
        if (isBackgroundError.value) {
          document.documentElement.classList.add('transparent-background')
          document.body.classList.add('transparent-background')
        } else {
          document.documentElement.classList.remove('transparent-background')
          document.body.classList.remove('transparent-background')
        }
      }
      
      // 初始设置
      updateBodyClass()
      
      // 监听变化
      const unwatch = watchEffect(() => {
        updateBodyClass()
      })
      
      // 监听语言变化并更新SEO
      const unwatchSEO = watchEffect(() => {
        updateSEOMeta(currentLanguage.value)
        addStructuredData(currentLanguage.value)
      })
      
      // 清理函数中移除监听
      onUnmounted(() => {
        unwatch()
        unwatchSEO()
        document.documentElement.classList.remove('transparent-background')
        document.body.classList.remove('transparent-background')
      })
    })
    
    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeyDown)
    })
    
    return {
      // 引用
      effectsLayerRef,
      
      // 状态
      currentQuote,
      currentBackground,
      isBackgroundError,
      isQuoteTransitioning,
      isQuoteLoading,
      isBackgroundLoading,
      currentEffect,
      isSettingsVisible,
      isControlsVisible,
      isPlaylistVisible,
      
      // 音乐相关
      musicCurrentTrack,
      musicIsPlaying,
      musicIsMuted,
      musicVolume,
      musicDuration,
      musicCurrentTime,
      musicIsLoading,
      musicUploadedTracks,
      musicAllTracks,
      musicCurrentTrackIndex,
      musicTogglePlay,
      musicNextTrack,
      musicToggleMute,
      musicSetVolume,
      
      // 处理函数
      handleRefreshQuote,
      handleRefreshBackground,
      handleToggleEffect,
      handleToggleBackground,
      handleUploadTrack,
      handleRemoveTrack,
      handlePlayTrack
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  height: 100%;
  overflow: hidden;
  position: fixed;
  width: 100%;
  background: #000000;
}

/* iOS PWA 支持 - 完全全屏填充，确保背景图片可见 */
@supports (-webkit-touch-callout: none) {
  @media (display-mode: standalone) {
    html, body {
      /* 确保html和body填充整个屏幕，完全延伸到屏幕边缘 */
      height: 100%;
      height: 100vh;
      height: 100dvh;
      overflow: hidden;
      margin: 0;
      padding: 0;
      /* 重要：iOS PWA模式下确保html背景为黑色 */
      background: #000000 !important;
      /* 确保完全填充viewport */
      width: 100%;
      width: 100vw;
    }
    
    body {
      /* 重要：iOS PWA模式下确保body背景为黑色 */
      background: #000000 !important;
      /* 重要：重置padding以确保全屏延伸 */
      padding: 0 !important;
      margin: 0 !important;
    }
    
    #app {
      /* app容器填充整个视口，完全延伸到屏幕实际边缘 */
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100vh;
      height: 100dvh;
      overflow: hidden;
      padding: 0;
      margin: 0;
      /* 重要：app容器必须透明，不能有任何背景 */
      background: transparent !important;
      /* 确保不会阻挡背景 */
      z-index: auto;
    }
    
    /* 内容容器 - 透明容器，不阻挡背景 */
    .content-container {
      /* 内容区域仍需要避开safe area以确保可读性 */
      padding-top: calc(60px + env(safe-area-inset-top));
      padding-bottom: calc(140px + env(safe-area-inset-bottom));
      padding-left: calc(40px + env(safe-area-inset-left));
      padding-right: calc(40px + env(safe-area-inset-right));
      /* 确保内容容器不阻挡背景 */
      position: relative;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100vh;
      height: 100dvh;
      /* 重要：内容容器必须透明 */
      background: transparent !important;
      z-index: 10;
    }
    
    /* 底部按钮safe area适配 */
    .settings-button {
      bottom: calc(20px + env(safe-area-inset-bottom));
    }
    
    .toggle-controls-button {
      bottom: calc(20px + env(safe-area-inset-bottom));
    }
    
    .action-buttons {
      bottom: calc(160px + env(safe-area-inset-bottom));
    }
    
    .music-player {
      bottom: calc(30px + env(safe-area-inset-bottom));
      left: calc(30px + env(safe-area-inset-left));
    }
    
    /* 顶部组件safe area适配 */
    .info-bar {
      top: calc(30px + env(safe-area-inset-top));
    }
    
    .language-selector {
      top: calc(30px + env(safe-area-inset-top));
    }
    
    .effects-control {
      top: calc(30px + env(safe-area-inset-top));
    }
    
    /* 移动端响应式适配 */
    @media (max-width: 768px) {
      .content-container {
        padding-top: calc(60px + env(safe-area-inset-top));
        padding-bottom: calc(140px + env(safe-area-inset-bottom));
        padding-left: calc(15px + env(safe-area-inset-left));
        padding-right: calc(15px + env(safe-area-inset-right));
      }
      
      .settings-button {
        bottom: calc(95px + env(safe-area-inset-bottom));
        right: calc(15px + env(safe-area-inset-right));
      }
      
      .toggle-controls-button {
        bottom: calc(95px + env(safe-area-inset-bottom));
        left: calc(15px + env(safe-area-inset-left));
      }
      
      .action-buttons {
        bottom: calc(95px + env(safe-area-inset-bottom));
        right: calc(15px + env(safe-area-inset-right));
      }
      
      .music-player {
        bottom: calc(20px + env(safe-area-inset-bottom));
        left: calc(15px + env(safe-area-inset-left));
      }
      
      .info-bar {
        top: calc(25px + env(safe-area-inset-top));
      }
      
      .language-selector {
        top: calc(25px + env(safe-area-inset-top));
      }
      
      .effects-control {
        top: calc(25px + env(safe-area-inset-top));
      }
    }
    
    @media (max-width: 480px) {
      .content-container {
        padding-top: calc(60px + env(safe-area-inset-top));
        padding-bottom: calc(140px + env(safe-area-inset-bottom));
        padding-left: calc(12px + env(safe-area-inset-left));
        padding-right: calc(12px + env(safe-area-inset-right));
      }
      
      .settings-button {
        bottom: calc(95px + env(safe-area-inset-bottom));
        right: calc(12px + env(safe-area-inset-right));
      }
      
      .toggle-controls-button {
        bottom: calc(95px + env(safe-area-inset-bottom));
        left: calc(12px + env(safe-area-inset-left));
      }
      
      .action-buttons {
        bottom: calc(95px + env(safe-area-inset-bottom));
        right: calc(12px + env(safe-area-inset-right));
      }
      
      .music-player {
        bottom: calc(20px + env(safe-area-inset-bottom));
        left: calc(12px + env(safe-area-inset-left));
      }
      
      .info-bar {
        top: calc(20px + env(safe-area-inset-top));
      }
      
      .language-selector {
        top: calc(20px + env(safe-area-inset-top));
      }
      
      .effects-control {
        top: calc(20px + env(safe-area-inset-top));
      }
    }
  }
}

/* Android PWA 全屏优化 */
@supports not (-webkit-touch-callout: none) {
  @media (display-mode: standalone) {
    html, body {
      overscroll-behavior: none;
      -webkit-overflow-scrolling: touch;
    }
    
    #app {
      min-height: 100vh;
      min-height: 100dvh;
    }
  }
}

body {
  font-family: 'Georgia', 'KaiTi', '楷体', serif;
  background: #000000;
  touch-action: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
}

html.transparent-background {
  background: #000000 !important;
}

body.transparent-background {
  background: #000000 !important;
}

#app {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  height: 100svh; /* 使用新的视口单位 */
  overflow: hidden;
  /* 确保app容器透明，让背景图片显示 */
  background: transparent;
}

.content-container {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  padding: 40px;
  text-align: center;
  /* 确保内容容器透明，不阻挡背景 */
  background: transparent;
}

/* 深色模式支持 - 使用黑色背景保持一致性 */
@media (prefers-color-scheme: dark) {
  body:not(.transparent-background) {
    background: #000000;
  }
}

/* 移动端响应式设计 */
@media (max-width: 768px) {
  .content-container {
    padding: 15px;
    justify-content: center;
    padding-top: 60px;
    padding-bottom: 140px;
  }
}

@media (max-width: 480px) {
  .content-container {
    padding: 12px;
    padding-top: 60px;
    padding-bottom: 140px;
  }
}

/* 横屏手机适配 */
@media (max-height: 500px) and (orientation: landscape) {
  .content-container {
    padding-top: 40px;
    padding-bottom: 80px;
    justify-content: center;
  }
}

/* 高分辨率屏幕优化 */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  * {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
}

/* 设置按钮样式 */
.settings-button {
  position: fixed;
  bottom: 90px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.settings-button:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.4);
  color: white;
  transform: scale(1.05);
}

.settings-button svg {
  width: 24px;
  height: 24px;
}

/* 功能控制按钮样式 */
.toggle-controls-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
}

.toggle-controls-button:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.4);
  color: white;
  transform: scale(1.05);
}

.toggle-controls-button svg {
  width: 24px;
  height: 24px;
}

/* 移动端按钮样式 */
@media (max-width: 768px) {
  .settings-button {
    bottom: 95px;
    right: 15px;
    width: 45px;
    height: 45px;
  }
  
  .settings-button svg {
    width: 22px;
    height: 22px;
  }
  
  .toggle-controls-button {
    bottom: 95px;
    left: 15px;
    width: 45px;
    height: 45px;
  }
  
  .toggle-controls-button svg {
    width: 22px;
    height: 22px;
  }
}

@media (max-width: 480px) {
  .settings-button {
    bottom: 95px;
    right: 12px;
    width: 40px;
    height: 40px;
  }
  
  .settings-button svg {
    width: 20px;
    height: 20px;
  }
  
  .toggle-controls-button {
    bottom: 95px;
    left: 12px;
    width: 40px;
    height: 40px;
  }
  
  .toggle-controls-button svg {
    width: 20px;
    height: 20px;
  }
}
</style>
